import {
  inferVmFromAddress,
  parsePortfolioAssetId,
} from "~/features/portfolio-domain";
import type { VmId } from "~/lib/auth/vm-types";
import {
  readFiniteInteger,
  readFiniteNumber,
  readString,
  toRecord,
} from "./portfolio-parsers";

export interface FundingAssetRow {
  address: string;
  assetId: string;
  balance: string;
  balanceUsd: number;
  chainId: number;
  decimals: number | null;
  name: string;
  symbol: string;
  vm: VmId;
}

export function isValidErc20Decimals(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 255;
}

export function parseTokenDecimals(value: unknown): number | null {
  const parsed = readFiniteInteger(value);
  if (parsed === undefined || !isValidErc20Decimals(parsed)) {
    return null;
  }

  return parsed;
}

export function normalizePortfolioAssetRows(input: {
  filteredTokenRows: unknown;
  userPortfolioRows: unknown;
}): FundingAssetRow[] {
  const rowsById = new Map<string, FundingAssetRow>();

  const writeRow = (next: FundingAssetRow) => {
    const existing = rowsById.get(next.assetId);
    if (!existing) {
      rowsById.set(next.assetId, next);
      return;
    }

    rowsById.set(next.assetId, {
      ...existing,
      balance: next.balance !== "0" ? next.balance : existing.balance,
      balanceUsd: next.balanceUsd > 0 ? next.balanceUsd : existing.balanceUsd,
      decimals: next.decimals ?? existing.decimals,
      name: next.name !== "Unknown" ? next.name : existing.name,
      symbol: next.symbol !== "UNKNOWN" ? next.symbol : existing.symbol,
    });
  };

  if (Array.isArray(input.userPortfolioRows)) {
    for (const row of input.userPortfolioRows) {
      const rowRecord = toRecord(row);
      const assetId = readString(rowRecord?.assetId);
      if (!assetId) {
        continue;
      }

      const parsedAsset = parsePortfolioAssetId(assetId);
      if (!parsedAsset) {
        continue;
      }

      const chainId = Number.parseInt(parsedAsset.chainRef, 10);
      if (!Number.isFinite(chainId)) {
        continue;
      }

      const tokenRecord = toRecord(rowRecord?.token);

      writeRow({
        address: parsedAsset.address,
        assetId,
        balance: readString(rowRecord?.baseTokenBalance) ?? "0",
        balanceUsd: readFiniteNumber(rowRecord?.balanceInUsd) ?? 0,
        chainId,
        decimals:
          parseTokenDecimals(rowRecord?.decimals) ??
          parseTokenDecimals(tokenRecord?.decimals),
        name:
          readString(rowRecord?.name) ??
          readString(tokenRecord?.name) ??
          "Unknown",
        symbol:
          readString(rowRecord?.symbol) ??
          readString(tokenRecord?.symbol) ??
          "UNKNOWN",
        vm: parsedAsset.vm,
      });
    }
  }

  if (Array.isArray(input.filteredTokenRows)) {
    for (const row of input.filteredTokenRows) {
      const rowRecord = toRecord(row);
      const address = readString(rowRecord?.address);
      const chainIdRaw = readFiniteInteger(rowRecord?.chainId);
      const assetId = readString(rowRecord?.assetId);
      if (!address || chainIdRaw === undefined || !assetId) {
        continue;
      }

      const parsedAsset = parsePortfolioAssetId(assetId);

      writeRow({
        address,
        assetId,
        balance: "0",
        balanceUsd: 0,
        chainId: chainIdRaw,
        decimals: parseTokenDecimals(rowRecord?.decimals),
        name: readString(rowRecord?.name) ?? "Unknown",
        symbol: readString(rowRecord?.symbol) ?? "UNKNOWN",
        vm: parsedAsset?.vm ?? inferVmFromAddress(address),
      });
    }
  }

  return Array.from(rowsById.values()).sort((left, right) => {
    if (left.balanceUsd !== right.balanceUsd) {
      return right.balanceUsd - left.balanceUsd;
    }

    return left.symbol.localeCompare(right.symbol);
  });
}
